/**
 * "Decide all" (docs/ux/project-tab-spec.md §1.6): one session's decisions ranked by leverage,
 * suggested answers preselected, each row expandable into the full decision view in place.
 * "Use suggested answers" submits every row not yet answered. Risky decisions never join the
 * batch submit; they open in full and ask for a passkey on their own.
 */
import { Button } from '@viko/transcript-ui'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { useEffect, useState } from 'react'
import type { DecisionView as DecisionModel } from '../../lib/decision-view.js'
import { errorText } from '../ui.js'
import { DecisionPanel, useDecisionActions, useDecisionModel } from './DecisionPanel.js'
import { NoteField, OptionKey, optionState, SuggestedPill, useNote } from './OptionList.js'

export function BatchView({
  title,
  keys,
  onDone,
}: {
  title: string
  keys: readonly string[]
  onDone?: () => void
}) {
  const act = useDecisionActions()
  const [choice, setChoice] = useState<Record<string, string>>({})
  const [notes, setNotes] = useState<Record<string, string>>({})
  const [views, setViews] = useState<Record<string, DecisionModel | undefined>>({})
  const [open, setOpen] = useState<string>()
  const [busy, setBusy] = useState(false)
  const pending = keys.filter((k) => views[k]?.open && !views[k]?.risky)
  const submit = async () => {
    setBusy(true)
    let n = 0
    try {
      for (const k of pending) {
        const v = views[k]
        const optionId = choice[k] ?? v?.suggested?.id
        if (!v || !optionId) continue
        const note = notes[k]?.trim()
        if (await act.answer(v, { mode: 'pick', optionId, ...(note ? { note } : {}) })) n++
      }
      act.toast(`Sent ${n} answer${n === 1 ? '' : 's'}`)
      onDone?.()
    } catch (e) {
      act.toast(errorText(e), 'error')
    } finally {
      setBusy(false)
    }
  }
  return (
    <section className="dv" aria-labelledby="dv-batch-h">
      <div className="dv__scroll">
        <header className="dv__head">
          <h2 id="dv-batch-h" className="dv__title">
            {title}
          </h2>
          <p className="dv-muted">
            Suggested answers are preselected. Change any, open one for its details and thread, or
            send them all.
          </p>
        </header>
        <ol className="dv-batch m-0 list-none !p-0">
          {keys.map((k, i) => (
            <BatchRow
              key={k}
              inspectKey={k}
              index={i}
              choice={choice[k]}
              expanded={open === k}
              onToggle={() => setOpen(open === k ? undefined : k)}
              onChoose={(id) => setChoice((c) => ({ ...c, [k]: id }))}
              onNote={(t) => setNotes((c) => (c[k] === t ? c : { ...c, [k]: t }))}
              onView={(v) => setViews((cur) => (cur[k] === v ? cur : { ...cur, [k]: v }))}
            />
          ))}
        </ol>
      </div>
      <section className="dv-bar" aria-label="Answer all">
        <div className="dv-bar__row">
          <Button
            variant="primary"
            size="sm"
            disabled={busy || !pending.length}
            onClick={() => void submit()}
          >
            {pending.length
              ? `Send ${pending.length} answer${pending.length === 1 ? '' : 's'}`
              : 'Nothing left to send'}
          </Button>
          <span className="dv-muted">
            {keys.length - pending.length > 0
              ? `${keys.length - pending.length} answered or need a passkey`
              : 'Use suggested answers, or pick per row'}
          </span>
        </div>
      </section>
    </section>
  )
}

function BatchRow({
  inspectKey,
  index,
  choice,
  expanded,
  onToggle,
  onChoose,
  onNote,
  onView,
}: {
  inspectKey: string
  index: number
  choice: string | undefined
  expanded: boolean
  onToggle: () => void
  onChoose: (optionId: string) => void
  onNote: (text: string) => void
  onView: (v: DecisionModel | undefined) => void
}) {
  const m = useDecisionModel(inspectKey)
  const v = m.view
  const note = useNote()
  // biome-ignore lint/correctness/useExhaustiveDependencies: report the draft up
  useEffect(() => onNote(note.text), [note.text])
  // Report the resolved view up (the batch submits from these).
  // biome-ignore lint/correctness/useExhaustiveDependencies: report when the view changes
  useEffect(() => onView(v), [v])
  if (!v) return null
  const chosen = choice ?? v.suggested?.id
  return (
    <li className="dv-batch__row" data-batch-row={index + 1}>
      <p className="dv-batch__q">{v.question}</p>
      {v.open ? (
        v.risky ? (
          <p className="dv-muted">{v.categoryLabel}: answer it on its own, with your passkey.</p>
        ) : (
          <>
            <div className="dv-batch__opts" role="group" aria-label={`Answer for “${v.title}”`}>
              {v.options.map((o) => {
                // Unfocused options look the same; the suggested one (sent unless you pick
                // another) carries only the pill. A pick is the strong accent state.
                const st = optionState(o, { picked: choice })
                return (
                  <button
                    key={o.id}
                    type="button"
                    className={`dv-batch__opt ${st.className}`}
                    aria-pressed={st.picked}
                    onClick={() => onChoose(o.id)}
                  >
                    <OptionKey n={o.key} picked={st.picked} />
                    {o.label}
                    {o.recommended ? <SuggestedPill /> : null}
                  </button>
                )
              })}
            </div>
            <NoteField
              note={note}
              target={v.options.find((o) => o.id === chosen)?.label}
              busy={false}
              showKeys={false}
              files={false}
            />
          </>
        )
      ) : (
        <p className="dv-muted">{v.answerText ?? 'Answered'}</p>
      )}
      <button type="button" className="dv-link" aria-expanded={expanded} onClick={onToggle}>
        {expanded ? (
          <ChevronDown size={14} aria-hidden="true" />
        ) : (
          <ChevronRight size={14} aria-hidden="true" />
        )}
        {expanded ? 'Hide details' : 'Details, evidence and thread'}
      </button>
      {expanded ? (
        <div className="dv-batch__detail">
          <DecisionPanel inspectKey={inspectKey} hotkeys={false} />
        </div>
      ) : null}
    </li>
  )
}
